//
// Copyright (c) 2011-2025 by Curalate, Inc.
//
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { PojoMap } from 'pojo-maps';
import { PojoSet } from 'pojo-sets';

import { addToGalleries } from '@apis/bulkActionsApi';
import { getApprovedReels } from '@fanreels/fanreelsApi';
import { galleriesDataActions } from '@galleries/reducers/galleriesDataSlice';
import { getAllInstaShopGalleries } from '@galleries/selectors/getAllInstaShopGalleries';
import { isSelfServeGallery } from '@galleries/utils/galleryTypeUtils';
import Cuid from '@models/Cuid';
import { ExperienceContainerType } from '@models/ExperienceContainerType';
import ReelId from '@models/ReelId';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { queryClient } from '@queries/ReactQueryProvider';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import * as shareApi from '@share/api/shareApi';
import { IApproveForGalleriesState } from '@uia/approve-for-galleries/approve-for-galleries-model';
import { getSelectedGalleries } from '@uia/approve-for-galleries/getSelectedGalleries';
import { getSelectedReelIds } from '@uia/approve-for-galleries/getSelectedReelIds';
import { getIsBazaarvoiceNetworkSyndicationFrontendEnabled } from '@uia/rights/selectors/getUIARights';

const initialState: IApproveForGalleriesState = {
  gallerySelection: {},
  isBazaarvoiceSyndicationSelected: true,
  isAddingToGalleries: false,
  approvedReels: [],
  fetchMetadata: {},
};

const { actions, reducer } = createSlice({
  name: SliceNames.UIAModalApproveForGalleries,
  initialState,
  reducers: {
    toggleGallery: (state, action: PayloadAction<ReelId>) => {
      const { gallerySelection } = state;
      const reelId = action.payload;

      if (PojoSet.has(gallerySelection, reelId)) {
        state.gallerySelection = PojoSet.remove(gallerySelection, reelId);
      } else {
        state.gallerySelection = PojoSet.add(gallerySelection, reelId);
      }
    },

    selectGallery: (state, action: PayloadAction<ReelId>) => {
      const { gallerySelection } = state;
      const reelId = action.payload;

      state.gallerySelection = PojoSet.add(gallerySelection, reelId);
    },

    toggleBazaarvoiceSyndication: (state) => {
      const { isBazaarvoiceSyndicationSelected } = state;

      state.isBazaarvoiceSyndicationSelected =
        !isBazaarvoiceSyndicationSelected;
    },

    startAddToGalleries: (state) => {
      state.isAddingToGalleries = true;
    },

    endAddToGalleries: (state) => {
      state.isAddingToGalleries = false;
    },

    requestApprovedReelsSucceeded: (state, action: PayloadAction<ReelId[]>) => {
      state.approvedReels = action.payload;
    },

    requestApprovedReelsFailed: (state, action: PayloadAction<string>) => {
      state.fetchMetadata.approvedReelsError = action.payload;
    },
  },
});

const {
  endAddToGalleries,
  requestApprovedReelsFailed,
  requestApprovedReelsSucceeded,
  selectGallery,
  startAddToGalleries,
  toggleBazaarvoiceSyndication,
  toggleGallery,
} = actions;

export const approveForGalleriesActions = {
  toggleGallery,
  selectGallery,
  toggleBazaarvoiceSyndication,

  requestApprovedReels: createThunk(() => async (dispatch, getState) => {
    const selectedCuid =
      getState().uiaModal.selectedItem.selectedItemMetadata.rootCuid;
    const cuidArr = [Cuid(selectedCuid)];

    try {
      const approvedReelsMap = await getApprovedReels(cuidArr);
      const approvedReels = PojoMap.get(approvedReelsMap, selectedCuid);
      if (approvedReels) {
        dispatch(requestApprovedReelsSucceeded(approvedReels));
      }
    } catch (_e) {
      dispatch(
        requestApprovedReelsFailed(
          'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  }),

  addItemToGalleries: createThunk(
    (shouldSkipRights?: boolean) => async (dispatch, getState) => {
      const hasFetchedGalleries =
        getState().galleries.data.galleries.orderedIds.length > 0;
      if (!hasFetchedGalleries) {
        await dispatch(galleriesDataActions.getGalleries());
      }

      dispatch(startAddToGalleries());
      const selectedGalleries = getSelectedGalleries(getState());
      const instaShopGalleryIsSelected = selectedGalleries.some(
        (gallery) =>
          isSelfServeGallery(gallery) &&
          gallery.containerType === ExperienceContainerType.INSTASHOP,
      );

      const reelIdsToApproveTo = (() => {
        if (instaShopGalleryIsSelected) {
          const allInstaShopReelIds = getAllInstaShopGalleries(getState()).map(
            (gallery) => gallery.reelId,
          );
          const selectedReelIds = getSelectedReelIds(getState());

          const allReelIdsToApproveToSet = PojoSet.from([
            ...allInstaShopReelIds,
            ...selectedReelIds,
          ]);

          return PojoSet.toArray(allReelIdsToApproveToSet);
        } else {
          return getSelectedReelIds(getState());
        }
      })();

      const selectedItemCuid =
        getState().uiaModal.selectedItem.selectedItemMetadata.rootCuid;

      const cuidsToApprove = [Cuid.toPartial(selectedItemCuid)];

      const isBazaarvoiceSyndicationEnabled =
        getIsBazaarvoiceNetworkSyndicationFrontendEnabled(getState());
      const isBazaarvoiceSyndicationSelected =
        getState().uiaModal.approveForGalleries
          .isBazaarvoiceSyndicationSelected;
      const shouldSyndicateToBazaarvoiceNetwork =
        isBazaarvoiceSyndicationEnabled && isBazaarvoiceSyndicationSelected;

      const promises = [];

      if (shouldSyndicateToBazaarvoiceNetwork) {
        promises.push(shareApi.shareMedia(cuidsToApprove));
      }

      promises.push(
        // Hack to appease the typescript gods.
        // Ensures we're pushing a promise of type Promise<void> to match the type of the Share promise above.
        addToGalleries(
          cuidsToApprove,
          reelIdsToApproveTo,
          shouldSkipRights,
        ).then((_res) => {
          return null;
        }),
      );

      await Promise.all(promises);

      dispatch(endAddToGalleries());
    },
  ),
};

export default reducer;
